import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * Kağan Şahin's contact page.
 *
 * Asked for 2026-09-15, with the address to build it at
 * (`/en/about/team/kagan-sahin`) and the details to put on it: the name, the
 * title, two phone numbers and the mail address. So the slug is theirs, not a
 * guess — an inbound link to it already exists somewhere.
 *
 * The details are the page: the title under the name, and each way of reaching
 * him as its own clickable card (`mailto:` / `tel:`), because a phone number
 * that is not a link is a number somebody retypes on a phone. The LinkedIn
 * profile the About Us card used to point at is kept here as the second action,
 * so nothing that was reachable before is lost.
 *
 * The name is spelled as he gave it — Kağan Şahin. The About Us grid still says
 * "Kagan Sahin" beside two other names in the same ASCII shape; changing all
 * three is a separate decision about that page.
 *
 * Asked for 2026-09-15: the team contact pages should read as one set, so this
 * one follows the shared shape — role in the eyebrow, one line of role under
 * the name, a `features` grid whose card title *is* the address or the number
 * and is itself the link, and the same closing band back to the firm. That is
 * why the role paragraph moved into `sub` and the grid cards carry `href`.
 */
export const route: RouteDef = {
  id: 'teamKaganSahin',
  path: 'about/team/kagan-sahin',
  // Asked for 2026-09-15: the page must not appear anywhere the site leads
  // people — it is handed out as a QR code and reached by that link alone. So
  // it is out of the sitemap and out of search results, and the About Us card
  // points back at LinkedIn as it did before. The URL itself still works.
  robots: 'noindex',
  priority: 0.5,
}

const EMAIL = 'kagan@digitopia.ai'
const LINKEDIN = 'https://www.linkedin.com/in/kagansahin/'
// Asked for 2026-09-17, for every team page: a button beside LinkedIn that
// saves the details straight into the reader's phone. The file is a static
// vCard in `public/vcf/`, named after this page's slug — see that folder's
// README.
const VCARD = '/vcf/kagan-sahin.vcf'

export const teamKaganSahin: WrittenPage = {
  navLabel: 'Kağan Şahin',
  title: 'Kağan Şahin: Co-CEO, Operations',
  description:
    'Contact Kağan Şahin, Co-CEO for Operations at Digitopia: email kagan@digitopia.ai, or call +1 302 607 4600 (United States) or +32 460 94 98 70 (Belgium).',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Co-CEO, Operations',
      headline: 'Kağan Şahin',
      // No portrait, asked for 2026-09-15: this page is scanned from a QR code
      // on a card that already has his face on it, so it is a set of details
      // rather than a profile.
      ctas: [
        { label: 'Email', href: `mailto:${EMAIL}` },
        { label: 'LinkedIn', href: LINKEDIN },
        { label: 'Save contact', href: VCARD },
      ],
    },
    {
      kind: 'features',
      heading: 'Get in touch',
      columns: 3,
      items: [
        {
          title: EMAIL,
          note: 'Email',
          href: `mailto:${EMAIL}`,
        },
        {
          title: '+1 302 607 4600',
          note: 'United States',
          href: 'tel:+13026074600',
        },
        {
          title: '+32 460 94 98 70',
          note: 'Belgium',
          href: 'tel:+32460949870',
        },
      ],
      footnote: 'Digitopia works from New York, London and Istanbul.',
    },
    {
      kind: 'cta',
      heading: 'Rather start with the firm than a person?',
      sub: 'The contact form reaches the whole team, and About Us says who else you would be working with.',
      tone: 'band',
      ctas: [
        { label: 'Contact Digitopia', href: LINKS.contact },
        { label: 'About Digitopia', href: LINKS.about },
      ],
    },
  ],
}
